import { useEffect, useRef, useState } from 'react';
import { readGenerationProgress } from '@/lib/generationProgress';

/**
 * Live progress for a generation, ready to render.
 *
 * Two jobs the pure model in @/lib/generationProgress can't do on its own:
 *
 *  - Keep time. Chunks are the only thing that re-renders the view, and the
 *    stream can go quiet for tens of seconds (prompt enhancement runs before the
 *    first token; a patched refinement emits nothing until it is done). A ticking
 *    clock keeps the elapsed counter and the time floor moving through the gaps.
 *  - Ratchet. The estimate sharpens as the nav reveals how many pages are coming,
 *    which can lower the raw number; the bar only ever moves forward.
 *
 * @param {string} streamedText
 * @param {boolean} isGenerating
 * @param {boolean} [isDone] the run finished successfully — pins the bar at 100.
 *   Passed rather than inferred from `!isGenerating`, so a failed run freezes
 *   where it stopped instead of reporting a site that was never built.
 * @param {object} [kind]
 * @param {boolean} [kind.edit]       a change to an existing site, not a build
 * @param {boolean} [kind.rewriting]  that change is being made by writing the
 *   whole site out again, after a patch could not make it
 */
export function useGenerationProgress(streamedText = '', isGenerating = false, isDone = false, { edit = false, rewriting = false } = {}) {
  const [elapsedMs, setElapsedMs] = useState(0);
  const startRef = useRef(0);
  const peakRef = useRef(0);
  const hasRunRef = useRef(false);
  const modeRef = useRef('');

  useEffect(() => {
    if (!isGenerating) return undefined;

    // A new run starts the clock over and releases the ratchet.
    startRef.current = Date.now();
    peakRef.current = 0;
    setElapsedMs(0);

    const id = setInterval(() => setElapsedMs(Date.now() - startRef.current), 1000);
    return () => {
      clearInterval(id);
      // Freeze on the real total rather than the last whole second.
      setElapsedMs(Date.now() - startRef.current);
    };
  }, [isGenerating]);

  // Both idempotent, so re-running them during a render is safe.
  if (isGenerating) hasRunRef.current = true;

  const progress = readGenerationProgress(streamedText, { elapsedMs, isDone, edit, rewriting });

  if (!hasRunRef.current) {
    return { ...progress, percent: 0, elapsedMs: 0, isGenerating: false };
  }

  // A fallback rewrite is new work, not more of the edit before it. Held at
  // the edit's peak, the bar would sit still for the minutes the rewrite takes.
  if (modeRef.current !== progress.mode) {
    modeRef.current = progress.mode;
    peakRef.current = 0;
  }

  const percent = Math.max(progress.percent, peakRef.current);
  peakRef.current = percent;

  return { ...progress, percent, elapsedMs, isGenerating };
}

export default useGenerationProgress;
